import {
  ComponentTemplateDto,
  ComponentType,
} from "@growth-machine-llc/stridist-api-client";
import { Box, IconButton, ListItemIcon, ListItemText } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { ComponentTemplateMenu } from "./ComponentTemplateMenu";
import { ComponentIcon } from "../program-component/ComponentIcon";
import { ArrowForwardRounded } from "@mui/icons-material";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { SelectComponentIconsMenu } from "../menu/SelectComponentIconsMenu";
import { getColorByComponentType } from "../program-calendar/utils/common";
import { ChangeComponentCoverDialog } from "../dialog/ChangeComponentCoverDialog";
import { useUpdateLibraryComponentTemplate } from "./hooks/useUpdateLibraryComponentTemplate";
import logo from "../../components/app/logo.svg";
import { clsx } from "clsx";
import { JourneyAnchors } from "../../constants";
import { generateThumbnailUrl } from "../../utils/thumbnail";

type StyleProps = {
  image: string;
  selected: boolean;
  isComponentImage: boolean;
  isPrivate?: boolean;
  borderColor: string;
};

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  cover: {
    backgroundImage: ({ image }) => `url("${image}")`,
    backgroundSize: ({ isComponentImage }) =>
      isComponentImage ? "cover" : "contain",
    backgroundColor: ({ isComponentImage }) =>
      isComponentImage ? "inherit" : theme.palette.background.default,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    width: theme.spacing(20),
    height: theme.spacing(10.75),
    marginRight: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    border: ({ borderColor, selected }) =>
      selected && `1px solid ${borderColor}`,
    filter: "drop-shadow(0px 5px 4px rgba(0, 0, 0, 0.05))",
    cursor: ({ isPrivate }) => (isPrivate ? "pointer" : "default"),
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: theme.spacing(-0.5),
    textOverflow: "ellipsis",
    overflow: "hidden",

    [theme.breakpoints.up("md")]: {
      marginRight: theme.spacing(10),
    },
  },
  wrapper: {
    display: "flex",
    padding: theme.spacing(1, 0),
    width: "100%",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: theme.spacing(1),
    justifyContent: "space-between",
    cursor: "pointer",
    flex: 1,
    border: ({ selected, borderColor }) =>
      `1px solid ${selected ? borderColor : theme.palette.border.primary}`,
    borderRadius: theme.spacing(1),
  },
  icon: {
    padding: theme.spacing(1),
    margin: theme.spacing(0, 1),
    cursor: "pointer",
    borderRadius: theme.spacing(1),
    "&:hover": {
      backgroundColor: ({ isPrivate }) =>
        isPrivate && theme.palette.selected.main,
    },
  },
  iconsMenu: {
    width: theme.spacing(40),
    zIndex: "9999 !important" as any,
  },
}));

export interface ComponentTemplateListItemProps {
  component: ComponentTemplateDto;
  selected?: boolean;
  type: ComponentType;
  onContentClick?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;

  onContentDoubleClick?: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;

  onEdit?: (value: boolean) => void;
  hideArrow?: boolean;
  hideImage?: boolean;
  className?: string;
}

const ComponentTemplateListItem = (props: ComponentTemplateListItemProps) => {
  const {
    component,
    selected,
    type,
    onEdit,
    onContentClick,
    onContentDoubleClick,
    hideArrow,
    hideImage,
    className,
  } = props;
  const borderColor = getColorByComponentType(type as ComponentType);

  const workspace = useCurrentWorkspace();
  const defaultLogo =
    generateThumbnailUrl({
      src: workspace?.logoUrl || logo,
      applyModifiers: true,
    }) || logo;

  const componentImage = component.image
    ? generateThumbnailUrl({
        src: component.image,
        applyModifiers: true,
      })
    : null;

  const s = useStyles({
    image: componentImage || defaultLogo,
    selected: selected || false,
    isComponentImage: Boolean(componentImage),
    isPrivate: component?.private,
    borderColor,
  });

  const iconsMenuState = usePopupState({
    variant: "popover",
    popupId: "iconsMenu",
  });

  const changeCoverDialogState = usePopupState({
    variant: "popover",
    popupId: "change-cover-dialog",
  });

  const { mutate: updateTemplate, isPending: updatingTemplate } =
    useUpdateLibraryComponentTemplate(component.id, component.type);

  const handleCoverChange = React.useCallback(
    (value: string) => {
      changeCoverDialogState.close();
      if (
        value === componentImage ||
        (!componentImage && value === defaultLogo)
      ) {
        return;
      }

      updateTemplate({
        ...component,
        image: value,
      });
    },
    [changeCoverDialogState],
  );

  const handleUpdateIcon = (iconName: string) => {
    iconsMenuState.close();
    if (iconName === component.iconName) {
      return;
    }
    updateTemplate({
      ...component,
      iconName,
    });
  };

  return (
    <>
      <Box className={clsx(s.wrapper, className)}>
        {!hideImage && (
          <Box
            className={clsx(s.cover, JourneyAnchors.COMPONENT_TEMPLATE_IMAGE)}
            {...(component.coachId ? bindTrigger(changeCoverDialogState) : {})}
          />
        )}
        <Box
          className={s.contentContainer}
          onClick={onContentClick}
          onDoubleClick={onContentDoubleClick}
          data-template-id={component.id}
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <ListItemIcon
              onClick={(e) => e.stopPropagation()}
              children={
                <ComponentIcon
                  variant="icon"
                  componentData={{ iconName: component.iconName, type: type }}
                  className={JourneyAnchors.COMPONENT_TEMPLATE_ICON}
                />
              }
              className={s.icon}
              {...(component.coachId ? bindTrigger(iconsMenuState) : {})}
            />
          </Box>
          <ListItemText
            primary={component.title}
            secondary={component.category && `Program: ${component.category}`}
            primaryTypographyProps={{
              className: s.title,
            }}
            secondaryTypographyProps={{
              className: s.subtitle,
            }}
          />
          <Box onClick={(e) => e.stopPropagation()} display="flex">
            {component.private && (
              <ComponentTemplateMenu component={component} onEdit={onEdit} />
            )}
            {!hideArrow && (
              <Box
                onClick={onContentClick}
                onDoubleClick={onContentDoubleClick}
                data-template-id={component.id}
              >
                <IconButton children={<ArrowForwardRounded />} size="large" />
              </Box>
            )}
          </Box>
        </Box>{" "}
      </Box>
      <Box onClick={(e) => e.stopPropagation()}>
        <SelectComponentIconsMenu
          {...bindMenu(iconsMenuState)}
          className={s.iconsMenu}
          anchorOrigin={{
            vertical: "center",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
          onSubmit={(iconName) => {
            handleUpdateIcon(iconName);
          }}
          hideApplyToAll
          componentData={{
            type: component.type,
            iconName: component.iconName,
          }}
        />
      </Box>
      {changeCoverDialogState.isOpen && (
        <ChangeComponentCoverDialog
          open
          isTemplateLibrary
          onClose={changeCoverDialogState.close}
          componentData={
            {
              ...component,
              image: componentImage || defaultLogo,
            } as any
          }
          onCoverChange={handleCoverChange}
          workspaceLogo={defaultLogo}
        />
      )}
    </>
  );
};

export default ComponentTemplateListItem;
